import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Reveal, Overline } from "@/components/public/Reveal";
import api from "@/lib/api";

const Stars = ({ value = 0, size = 16 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-[#FBBC04] text-[#FBBC04]" : "fill-stone/20 text-stone/20"} />
    ))}
  </div>
);

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setData(r.data)).catch(() => {}); }, []);

  const reviews = (data?.reviews || []);
  if (!data || reviews.length === 0) return null;
  const loop = reviews.length > 2 ? [...reviews, ...reviews] : reviews;

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="google-reviews">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <Overline>Ils nous font confiance</Overline>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl text-forest leading-tight">Ce que disent nos clients</h2>
        </div>
        <div className="flex items-center gap-4 rounded-2xl bg-white border border-border px-6 py-4 shadow-sm">
          <img src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-6 w-auto" />
          <div className="h-8 w-px bg-border" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display text-3xl text-forest leading-none">{data.rating}</span>
              <Stars value={data.rating} size={18} />
            </div>
            <p className="text-xs text-stone mt-1">{data.total} avis Google</p>
          </div>
        </div>
      </Reveal>

      <div className="relative overflow-hidden reviews-marquee" data-testid="reviews-carousel">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-cream to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-cream to-transparent z-10" />
        <div className={`flex gap-6 w-max ${reviews.length > 2 ? "reviews-track" : ""}`}>
          {loop.map((r, i) => (
            <div key={`${r.id}-${i}`}
              className="flex flex-col shrink-0 w-[320px] rounded-[22px] bg-white border border-border p-6 hover:shadow-xl hover:shadow-forest/5 transition-all"
              data-testid={`review-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                {r.avatar ? <img src={r.avatar} alt={r.author} className="h-10 w-10 rounded-full object-cover" />
                  : <div className="h-10 w-10 rounded-full bg-brand/10 flex items-center justify-center font-semibold text-brand">{(r.author || "?")[0]}</div>}
                <div className="min-w-0">
                  <p className="font-semibold text-forest text-sm truncate">{r.author}</p>
                  <p className="text-xs text-stone">{r.date}</p>
                </div>
              </div>
              <Stars value={r.rating} />
              <p className="mt-3 text-sm text-stone leading-relaxed line-clamp-5 flex-1">
                {r.snippet || <span className="italic text-stone/70">A attribué une note de {r.rating}/5 ★</span>}
              </p>
            </div>
          ))}
        </div>
      </div>

      <Reveal className="mt-12 text-center">
        <p className="text-stone mb-4">Vous avez fait appel à nos services ? Partagez votre expérience 🌿</p>
        <a href="https://search.google.com/local/writereview?placeid=ChIJeQXeJffoyRIRzPoTgU20Ci0"
          target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn"
          className="inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-cream hover:bg-brand transition-colors">
          <Star size={17} className="fill-cream" /> Laisser un avis Google
        </a>
      </Reveal>
    </section>
  );
}
